import api from './api';
import { unwrapList, unwrapObject, type ListResult } from './api-envelope';
import type { B2BPriceListQuery } from './queryKeys';
import type { B2BChannelType, B2BRegion } from '@tcr-erp/shared';

export type B2BPriceListStatus = 'DRAFT' | 'PENDING' | 'ACTIVE' | 'EXPIRED';
export type B2BPriceTierConditionType = 'NONE' | 'QUANTITY' | 'REVENUE';
// TCR-2044 — chiết khấu tham khảo lưu kèm cấp giá (không áp dụng cho RETAIL). Giá trị THAM KHẢO,
// KHÔNG tự tính lại items[].prices[tier_key] — giá từng SKU/cấp vẫn nhập tay như cũ.
export type B2BPriceTierDiscountType = 'PERCENT' | 'VND';

export interface B2BPriceTierDefinition {
  tier_key: string;
  tier_name: string;
  condition_type: B2BPriceTierConditionType;
  condition_value: number;
  condition_unit?: string;
  sort_order: number;
  discount_type?: B2BPriceTierDiscountType | null;
  discount_value?: number | null;
}

export interface B2BPriceListItem {
  variant_id: string;
  product_id: string;
  sku: string;
  product_name: string;
  prices: Record<string, number>;
  prices_ex_vat?: Record<string, number>;
  /** Giá catalog hiện tại của variant — chỉ có trên GET detail, không gửi khi create/update. */
  base_price?: number;
  /**
   * TCR-2410 — GIÁ GỐC (trước chiết khấu) theo từng cấp giá, tuỳ chọn. Khi dòng có field này,
   * `prices[tier]` là giá CUỐI đã trừ CK; `/b2b/price-lists/suggest` sẽ trả `unit_price` = giá gốc
   * kèm `discount_type/discount_value` bên dưới để POS đổ CK vào cột Chiết khấu trên giá gốc.
   * Dòng KHÔNG có field mới ⇒ mọi luồng giữ nguyên hành vi cũ (giá cuối là đơn giá áp dụng).
   */
  list_prices?: Record<string, number>;
  /** TCR-2410 — loại chiết khấu RIÊNG THEO SKU của dòng này (cùng định dạng CK cấp giá TCR-2044). */
  discount_type?: B2BPriceTierDiscountType | null;
  /** TCR-2410 — giá trị chiết khấu theo SKU (% khi PERCENT, VND khi VND), đi kèm `discount_type`. */
  discount_value?: number | null;
}

/** TCR-2410 — 1 dòng nhập "giá gốc + CK" theo SKU cho `POST /b2b/price-lists/:id/import-list-prices`. */
export interface B2BPriceListImportListPriceRow {
  sku: string;
  /** Giá gốc trước chiết khấu (cột "Đơn giá" trong file CRM). */
  list_price: number;
  discount_type: B2BPriceTierDiscountType;
  discount_value: number;
}

/** Body nhập giá gốc + CK theo SKU (BE đang làm song song, hợp đồng đã chốt). */
export interface B2BPriceListImportListPricesBody {
  /** Cấp giá đích so sánh/cập nhật (vd RETAIL) — tuỳ chọn theo hợp đồng. */
  tier_key?: string;
  rows: B2BPriceListImportListPriceRow[];
  /** `true` = chỉ kiểm tra và trả kết quả, không ghi DB (bước xem trước). */
  dry_run?: boolean;
}

export type B2BPriceListImportListPriceStatus =
  | 'updated' // đã nhập thật (dry_run=false)
  | 'valid' // dry_run: khớp — giá gốc + CK cho ra đúng giá cuối đang lưu
  | 'not_found' // SKU không có trong bảng giá
  | 'mismatch' // lệch giá — giá cuối suy ra khác giá đang lưu
  | 'error'; // lỗi khác (báo trong message)

export interface B2BPriceListImportListPriceResult {
  sku: string;
  status: B2BPriceListImportListPriceStatus;
  message?: string;
  list_price?: number;
  discount_value?: number;
  /** Giá cuối = giá gốc − CK (BE tính lại để FE đối chiếu). */
  net_price?: number;
}

export interface B2BPriceListImportListPricesResponse {
  summary: {
    total: number;
    updated: number;
    valid: number;
    not_found: number;
    mismatch: number;
    error: number;
  };
  results: B2BPriceListImportListPriceResult[];
}

export interface B2BPriceListType {
  _id: string;
  brand_id: string;
  brand_name: string;
  name: string;
  description?: string;
  effective_from: string;
  effective_until?: string | null;
  status: B2BPriceListStatus;
  version: number;
  tier_definitions: B2BPriceTierDefinition[];
  items: B2BPriceListItem[];
  vat_included?: boolean;
  vat_rate?: number;
  created_at?: string;
  updated_at?: string;
  rejection_reason?: string;
  rejected_by_name?: string;
  rejected_at?: string | null;
  target_channels?: B2BChannelType[];
  target_regions?: B2BRegion[];
}

export interface B2BPriceListInput {
  brand_id: string;
  brand_name: string;
  name: string;
  description?: string;
  effective_from: string;
  effective_until?: string | null;
  vat_included?: boolean;
  vat_rate?: number;
  tier_definitions: B2BPriceTierDefinition[];
  items: B2BPriceListItem[];
  target_channels?: B2BChannelType[];
  target_regions?: B2BRegion[];
}

export interface B2BSuggestedPriceDisplay {
  retail_price?: number;
  trade_discount_amount?: number;
  trade_discount_percent?: number;
  condition_family?: 'NONE' | 'QUANTITY' | 'REVENUE' | null;
  quantity?: number;
  monthly_quantity?: number | null;
  monthly_revenue?: number | null;
  evaluation_basis?: {
    type?: string;
    quantity?: number;
    monthly_quantity?: number;
    monthly_revenue?: number;
    message?: string;
  };
  next_tier?: {
    tier_key: string;
    tier_name: string;
    condition_value: number;
    condition_unit: string;
    remaining: number;
  } | null;
  tiers?: Array<{
    tier_key: string;
    tier_name: string;
    unit_price: number;
    condition_type: string;
    condition_value: number;
    condition_unit: string;
    is_eligible: boolean;
    is_applied: boolean;
  }>;
  /**
   * TCR-1256: BE luôn trả 3 field này trong `display` (b2b-price-list.service.ts
   * BE, dòng ~801-804 nhánh CUSTOMER_OVERRIDE, ~932-935 nhánh bảng giá thường,
   * ~1007-1009 nhánh CK % toàn brand) nhưng FE type trước đây thiếu khai báo —
   * khiến field bị TypeScript "vô hình" dù đã thực sự có trên network response
   * (grep FE cho display.sku/display.product_name/display.prices: 0 kết quả sử
   * dụng — chưa hiển thị ở đâu, chỉ bổ sung type cho khớp response thật).
   */
  sku?: string | null;
  product_name?: string | null;
  /** Map tier_key → giá tuyệt đối; null ở nhánh CUSTOMER_OVERRIDE/CK % brand (không có bảng giá gốc để trích). */
  prices?: Record<string, number> | null;
}

export interface B2BSuggestedPrice {
  price_list_id: string | null;
  price_list_name: string;
  tier_key: string;
  tier_name?: string;
  unit_price: number;
  override_source?: 'CUSTOMER_OVERRIDE' | null;
  override_id?: string | null;
  price_list_version?: number | null;
  /**
   * TCR-1364: VAT của bảng giá đặc biệt (chỉ có ở nhánh ACTIVE PRICE LIST —
   * `null` ở nhánh CUSTOMER_OVERRIDE/CK % brand, không có VAT riêng cấp bảng
   * giá). FE dùng để tự set `item.vatOverride` theo bảng giá, xem
   * `suggestPosB2BPrices.ts`.
   */
  vat_rate?: number | null;
  vat_included?: boolean | null;
  /**
   * TCR-2126: CK mặc định của cấp giá đang áp (TCR-2044). `null` ở RETAIL, cấp không cấu hình
   * CK và nhánh CUSTOMER_OVERRIDE/CK % brand. POS đổ vào cột Chiết khấu, áp trên `unit_price`.
   */
  discount_type?: B2BPriceTierDiscountType | null;
  discount_value?: number | null;
  display?: B2BSuggestedPriceDisplay;
}


export type B2BPriceListCompareChangeType = 'UNCHANGED' | 'CHANGED' | 'ADDED' | 'REMOVED';

export interface B2BPriceListCompareMeta {
  _id: string;
  name: string;
  version: number;
  status: B2BPriceListStatus;
  brand_id: string;
  brand_name: string;
  effective_from: string;
  effective_until?: string | null;
  tier_definitions: B2BPriceTierDefinition[];
  item_count: number;
}

export interface B2BPriceListCompareTierDiff {
  tier_key: string;
  tier_name: string;
  price_a: number | null;
  price_b: number | null;
  delta: number | null;
  delta_percent: number | null;
}

export interface B2BPriceListCompareTierConditionDiff {
  tier_key: string;
  tier_name: string;
  sort_order: number;
  change_type: B2BPriceListCompareChangeType;
  condition_type_a: B2BPriceTierConditionType | null;
  condition_type_b: B2BPriceTierConditionType | null;
  condition_value_a: number | null;
  condition_value_b: number | null;
  condition_unit_a: string | null;
  condition_unit_b: string | null;
  value_delta: number | null;
}

export interface B2BPriceListCompareRow {
  variant_id: string;
  product_id: string;
  sku: string;
  product_name: string;
  change_type: B2BPriceListCompareChangeType;
  tiers: B2BPriceListCompareTierDiff[];
}

export interface B2BPriceListCompareResult {
  list_a: B2BPriceListCompareMeta;
  list_b: B2BPriceListCompareMeta;
  summary: {
    total_skus: number;
    changed_skus: number;
    added_skus: number;
    removed_skus: number;
    unchanged_skus: number;
  };
  tier_condition_diffs?: B2BPriceListCompareTierConditionDiff[];
  rows: B2BPriceListCompareRow[];
}

export interface B2BPriceListHistoryItem {
  _id: string;
  name: string;
  version: number;
  status: B2BPriceListStatus;
  brand_id: string;
  brand_name: string;
  effective_from: string;
  effective_until?: string | null;
  item_count: number;
  tier_count: number;
  created_by?: string | null;
  created_by_name?: string;
  created_at?: string | null;
  submitted_by?: string | null;
  submitted_by_name?: string;
  submitted_at?: string | null;
  approved_by?: string | null;
  approved_by_name?: string;
  approved_at?: string | null;
  rejected_by?: string | null;
  rejected_by_name?: string;
  rejected_at?: string | null;
  rejection_reason?: string;
  change_summary?: {
    changed_skus: number;
    added_skus: number;
    removed_skus: number;
    unchanged_skus: number;
  } | null;
  previous_price_list_id?: string | null;
  previous_version?: number | null;
}

export interface B2BPriceListSnapshotResult {
  at_date: string;
  price_list: B2BPriceListType | null;
  message?: string | null;
}

/**
 * TCR-2017 — phạm vi kênh của user trên màn Bảng giá (GET /b2b/price-lists/my-scope).
 * `restricted: true, channel: null` = có quyền "chỉ kênh mình" nhưng tài khoản chưa gán Kênh B2B.
 */
export interface B2BPriceListViewScope {
  restricted: boolean;
  channel: B2BChannelType | null;
  /** TCR-2075 — mọi Kênh B2B của tài khoản (BE cũ không trả ⇒ rơi về `channel`). */
  channels?: B2BChannelType[];
}

export const b2bPriceListService = {
  myScope: (): Promise<B2BPriceListViewScope> =>
    api.get('/b2b/price-lists/my-scope').then(unwrapObject<B2BPriceListViewScope>),

  list: (params: B2BPriceListQuery): Promise<ListResult<B2BPriceListType>> =>
    api.get('/b2b/price-lists', { params }).then(unwrapList<B2BPriceListType>),

  getById: (id: string): Promise<B2BPriceListType> =>
    api.get(`/b2b/price-lists/${id}`).then(unwrapObject<B2BPriceListType>),

  compare: (params: { price_list_a_id: string; price_list_b_id: string }): Promise<B2BPriceListCompareResult> =>
    api.get('/b2b/price-lists/compare', { params }).then(unwrapObject<B2BPriceListCompareResult>),

  history: (params: { brand_id: string; page?: number; limit?: number }): Promise<ListResult<B2BPriceListHistoryItem>> =>
    api.get('/b2b/price-lists/history', { params }).then(unwrapList<B2BPriceListHistoryItem>),

  snapshot: (params: { brand_id: string; at_date: string }): Promise<B2BPriceListSnapshotResult> =>
    api.get('/b2b/price-lists/snapshot', { params }).then(unwrapObject<B2BPriceListSnapshotResult>),

  suggest: (params: {
    brand_id: string;
    variant_id: string;
    tier_key?: string;
    customer_id?: string;
    quantity?: number;
    include_current_qty?: boolean;
  }): Promise<B2BSuggestedPrice | null> =>
    api.get('/b2b/price-lists/suggest', { params }).then((res) => unwrapObject<B2BSuggestedPrice | null>(res)),

  create: (body: B2BPriceListInput): Promise<B2BPriceListType> =>
    api.post('/b2b/price-lists', body).then(unwrapObject<B2BPriceListType>),

  update: (id: string, body: B2BPriceListInput): Promise<B2BPriceListType> =>
    api.put(`/b2b/price-lists/${id}`, body).then(unwrapObject<B2BPriceListType>),

  submit: (id: string): Promise<B2BPriceListType> =>
    api.patch(`/b2b/price-lists/${id}/submit`).then(unwrapObject<B2BPriceListType>),

  activate: (id: string): Promise<B2BPriceListType & { overlap_warning?: string; archived_price_lists?: Array<{ _id: string; name: string }> }> =>
    api.patch(`/b2b/price-lists/${id}/activate`).then(
      unwrapObject<B2BPriceListType & { overlap_warning?: string; archived_price_lists?: Array<{ _id: string; name: string }> }>,
    ),

  reject: (id: string, body: { reason: string }): Promise<B2BPriceListType> =>
    api.patch(`/b2b/price-lists/${id}/reject`, body).then(unwrapObject<B2BPriceListType>),

  overlaps: (id: string): Promise<{
    price_list_id: string;
    overlapping: Array<{
      _id: string;
      name: string;
      version: number;
      effective_from: string;
      effective_until?: string | null;
    }>;
  }> => api.get(`/b2b/price-lists/${id}/overlaps`).then(
    unwrapObject<{
      price_list_id: string;
      overlapping: Array<{
        _id: string;
        name: string;
        version: number;
        effective_from: string;
        effective_until?: string | null;
      }>;
    }>,
  ),

  recall: (id: string): Promise<B2BPriceListType> =>
    api.patch(`/b2b/price-lists/${id}/recall`).then(unwrapObject<B2BPriceListType>),

  archive: (id: string): Promise<B2BPriceListType> =>
    api.patch(`/b2b/price-lists/${id}/archive`).then(unwrapObject<B2BPriceListType>),

  clone: (id: string): Promise<B2BPriceListType> =>
    api.post(`/b2b/price-lists/${id}/clone`).then(unwrapObject<B2BPriceListType>),

  /**
   * TCR-2410 — nhập "giá gốc + CK" theo SKU từ file CRM vào bảng giá.
   * `dry_run: true` = bước xem trước (BE chỉ kiểm tra, không ghi).
   */
  importListPrices: (id: string, body: B2BPriceListImportListPricesBody): Promise<B2BPriceListImportListPricesResponse> =>
    api.post(`/b2b/price-lists/${id}/import-list-prices`, body).then(unwrapObject<B2BPriceListImportListPricesResponse>),

  remove: (id: string): Promise<void> =>
    api.delete(`/b2b/price-lists/${id}`).then(() => undefined),
};
